import React from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ProgressRing } from "@/components/ProgressRing";
import { timeAgo, fmtDate } from "@/lib/format";
import { FileText, Share2, Trash2, HardHat, Archive, ArchiveRestore, Eye, CalendarClock, Bell } from "lucide-react";
import { cn } from "@/lib/utils";

const SWIPE_TRIGGER = 96;

function Row({ report, isMaster, onOpen, onShare, onDelete, onArchive, onNudge }) {
  const [imgErr, setImgErr] = React.useState(false);
  // A cached job response can hold an expired signed thumb URL that 404s on load.
  // When revalidation delivers a fresh URL, clear the error so the image recovers
  // without needing to open the report and come back.
  React.useEffect(() => { setImgErr(false); }, [report.thumb_url]);
  const dragged = React.useRef(false);
  const x = useMotionValue(0);
  const archiveOpacity = useTransform(x, [-SWIPE_TRIGGER, -24, 0], [1, 0.45, 0]);
  const deleteOpacity = useTransform(x, [0, 24, SWIPE_TRIGGER], [0, 0.45, 1]);
  const p = report.progress || {};
  const isChild = !!report.parent_report_id;
  const photoCount = p.photo_count || 0;
  const total = p.total || 0;
  const showThumb = report.thumb_url && !imgErr;
  const views = report.stats?.total || 0;
  const st = report.status;
  const isSubmitted = ["submitted", "approved", "changes_requested"].includes(st);
  const isSent = st === "sent" || (isChild && (st === "draft" || st === "pending"));
  const isComplete = total > 0 && (p.pct ?? 0) >= 100;
  const dueDate = report.due_date ? new Date(`${String(report.due_date).slice(0, 10)}T00:00:00`) : null;
  const todayD = new Date(); todayD.setHours(0, 0, 0, 0);
  const dueOverdue = dueDate && dueDate < todayD && !isComplete && !isSubmitted;
  const fmtDue = dueDate ? dueDate.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
  const statusBadge = isComplete
    ? { label: "Complete", cls: "bg-emerald-500/20 text-emerald-500" }
    : isSubmitted
    ? { label: "Back", cls: "bg-amber-500/20 text-amber-500" }
    : views > 0
    ? { label: "Opened", cls: "bg-sky-500/20 text-sky-400" }
    : isSent
    ? { label: "Sent", cls: "bg-emerald-500/20 text-emerald-500" }
    : { label: "Draft", cls: "bg-slate-500/20 text-slate-400" };

  const stop = (e, fn) => { e.stopPropagation(); fn && fn(); };
  const handleOpen = () => { if (dragged.current) return; onOpen && onOpen(); };

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Delete — revealed by dragging RIGHT */}
      <motion.div style={{ opacity: deleteOpacity }} className="absolute inset-y-0 left-0 z-0 flex w-1/2 items-center justify-start bg-rose-500 pl-5 text-white" data-testid={`report-swipe-delete-${report.id}`}>
        <div className="flex flex-col items-center gap-1"><Trash2 className="h-5 w-5" /><span className="text-[11px] font-semibold">Delete</span></div>
      </motion.div>
      {/* Archive — revealed by dragging LEFT */}
      <motion.div style={{ opacity: archiveOpacity }} className="absolute inset-y-0 right-0 z-0 flex w-1/2 items-center justify-end bg-emerald-500 pr-5 text-white" data-testid={`report-swipe-archive-${report.id}`}>
        <div className="flex flex-col items-center gap-1">{report.archived ? <ArchiveRestore className="h-5 w-5" /> : <Archive className="h-5 w-5" />}<span className="text-[11px] font-semibold">{report.archived ? "Unarchive" : "Archive"}</span></div>
      </motion.div>

      {/* draggable foreground */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.9}
        whileTap={{ scale: 0.97 }}
        style={{ x }}
        onDragStart={() => { dragged.current = true; }}
        onDragEnd={(e, info) => {
          if (info.offset.x <= -SWIPE_TRIGGER) onArchive && onArchive();
          else if (info.offset.x >= SWIPE_TRIGGER) onDelete && onDelete();
          setTimeout(() => { dragged.current = false; }, 60);
        }}
        role="button"
        tabIndex={0}
        data-testid={`report-card-${report.id}`}
        onClick={handleOpen}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleOpen()}
        className={cn(
          "card-elevated relative z-10 flex touch-pan-y cursor-pointer items-center rounded-xl border border-border bg-card text-left",
          isChild ? "gap-2.5 p-2.5 border-l-[3px] border-l-slate-400/40" : "gap-3 p-3",
          isMaster && "border-l-[5px] border-l-emerald-500"
        )}
      >
      {/* 1 — thumbnail */}
      <div className={cn("shrink-0 overflow-hidden rounded-lg border border-border bg-muted", isChild ? "h-12 w-12" : "h-14 w-14")}>
        {showThumb ? (
          <img src={report.thumb_url} alt="" onError={() => setImgErr(true)} className="h-full w-full object-cover transition-[filter] duration-150 active:brightness-110" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground/60">
            <FileText className="h-6 w-6" />
          </div>
        )}
      </div>

      {/* 2 — content */}
      <div className="min-w-0 flex-1">
        {isChild && report.dispatched_trade && (
          <div className="mb-1 inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-500" data-testid={`report-trade-${report.id}`}>
            <HardHat className="h-3 w-3" />
            {report.dispatched_trade}{report.dispatched_vendor_name ? ` · ${report.dispatched_vendor_name}` : ""}
          </div>
        )}

        <div className={cn("truncate font-semibold text-foreground", isChild ? "text-sm" : "text-base")}>
          {report.title || "Untitled report"}
        </div>

        <span className={cn("mt-1 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide", statusBadge.cls)} data-testid={`report-status-${report.id}`}>
          {statusBadge.label}
        </span>

        {report.submitted_by_email && (
          <div className="mt-0.5 truncate text-[13px] font-medium text-emerald-500">By {report.submitted_by_email}</div>
        )}

        <div className="mt-0.5 text-[13px] text-muted-foreground">
          {photoCount} {photoCount === 1 ? "photo" : "photos"} · {total} {total === 1 ? "item" : "items"}
        </div>

        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground/80">
          <span>{report.submitted_at ? `Sent ${timeAgo(report.submitted_at)}` : `Created ${fmtDate(report.created_at)}`}</span>
          {(report.stats?.total || 0) > 0 && (
            <span className="inline-flex items-center gap-1 font-medium text-emerald-500" data-testid={`report-views-${report.id}`}>
              <Eye className="h-3 w-3" /> {report.stats.total}
            </span>
          )}
          {dueDate && (
            <span className={cn("inline-flex items-center gap-1 font-medium", dueOverdue ? "text-rose-500" : "text-muted-foreground")} data-testid={`report-due-${report.id}`}>
              <CalendarClock className="h-3 w-3" /> {dueOverdue ? "Overdue" : `Due ${fmtDue}`}
            </span>
          )}
        </div>
      </div>

      {/* 3 — dual-arc ring (tap absorbed) */}
      {total > 0 && (
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <ProgressRing total={total} green={p.verified ?? p.done ?? 0} orange={p.pending_review || 0} red={p.issues || 0} size={44} stroke={4} />
        </div>
      )}

      {/* 4 — actions: nudge (once sent) + share */}
      {onNudge && (isSent || isSubmitted || views > 0) && (
        <button
          data-testid={`report-nudge-${report.id}`}
          aria-label="Nudge worker"
          onClick={(e) => stop(e, onNudge)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-amber-500 transition-colors hover:bg-amber-500/10"
        >
          <Bell className="h-[18px] w-[18px]" />
        </button>
      )}
      <button
        data-testid={`report-share-${report.id}`}
        aria-label="Share report"
        onClick={(e) => stop(e, onShare)}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-emerald-500 transition-colors hover:bg-emerald-500/10"
      >
        <Share2 className="h-[18px] w-[18px]" />
      </button>
      </motion.div>
    </div>
  );
}

export const ReportCard = React.memo(Row, (a, b) => {
  const x = a.report, y = b.report;
  return (
    a.isMaster === b.isMaster &&
    x.id === y.id &&
    x.title === y.title &&
    x.status === y.status &&
    x.due_date === y.due_date &&
    x.thumb_url === y.thumb_url &&
    x.submitted_at === y.submitted_at &&
    x.submitted_by_email === y.submitted_by_email &&
    x.parent_report_id === y.parent_report_id &&
    x.archived === y.archived &&
    x.dispatched_trade === y.dispatched_trade &&
    x.dispatched_vendor_name === y.dispatched_vendor_name &&
    (x.stats?.total || 0) === (y.stats?.total || 0) &&
    JSON.stringify(x.progress) === JSON.stringify(y.progress)
  );
});
